import React, { ComponentProps, useMemo } from 'react';
import { getDayjs, TIME_FORMAT } from '../../utils';
import { TimePicker } from 'antd';
import {
  Controller,
  FieldPath,
  FieldValues,
  useFormContext,
} from 'react-hook-form';
import { useAdditionalFormContext } from '../AdditionalFormContext';
import { useGetIdFromName } from '../utils';
import { FORM_DATE_PICKER_POPUP_CLASSNAME } from './constants';

export type FormTimePickerProps<
  FormData extends FieldValues,
  Name extends FieldPath<FormData> = FieldPath<FormData>
> = {
  name: Name;
  timeFormat?: string;
} & ComponentProps<typeof TimePicker>;

export const FormTimePicker = <
  FormData extends FieldValues,
  Name extends FieldPath<FormData>
>(
  props: FormTimePickerProps<FormData, Name>
) => {
  const getIdFromName = useGetIdFromName();
  const { name, id = getIdFromName(name), timeFormat, ...rest } = props;
  const { control } = useFormContext<FormData>();
  const format = useMemo(() => timeFormat ?? TIME_FORMAT, [timeFormat]);
  const { onChange: formOnChange } = useAdditionalFormContext();

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange } }) => (
        <TimePicker
          {...rest}
          id={id}
          format={format}
          onChange={(val) => {
            const time = val?.format(format) ?? null;
            onChange({
              target: { value: time },
            });
            formOnChange?.();
          }}
          classNames={{
            popup: { root: FORM_DATE_PICKER_POPUP_CLASSNAME },
          }}
          value={value ? getDayjs(value, format) : null}
        />
      )}
    />
  );
};
